import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const CART_DRAWER_REFERENCE_ADDED_FLAG = '__cartDrawerReferenceItemAdded';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {string | null} */
  #lastAddedVariantId = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener('click', this.#handleReferenceRelatedClick, { capture: true });
    this.addEventListener('click', this.#stopReferenceControlClick);
    this.addEventListener('submit', this.#handleReferenceRelatedSubmit);
    this.#syncAddedMessage();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener('click', this.#handleReferenceRelatedClick, { capture: true });
    this.removeEventListener('click', this.#stopReferenceControlClick);
    this.removeEventListener('submit', this.#handleReferenceRelatedSubmit);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#selectReferenceLineItem();
    this.#syncAddedMessage();
  }

  #handleCartAdd = (event) => {
    const updateSource = event.detail?.data?.source;
    const isProductAdd =
      (updateSource === 'product-form-component' || updateSource === 'cart-drawer-reference-related') &&
      !event.detail?.data?.didError;

    if (isProductAdd && event.detail?.sourceId) {
      this.#lastAddedVariantId = String(event.detail.sourceId);
      window[CART_DRAWER_REFERENCE_ADDED_FLAG] = true;
      this.toggleAttribute('item-added', true);
      this.#selectReferenceLineItem();
      this.#syncAddedMessageAfterRender();
    } else if (updateSource === 'cart-items-component') {
      this.#syncAddedMessageAfterRender();
    } else if (event.detail?.data?.didError) {
      window[CART_DRAWER_REFERENCE_ADDED_FLAG] = false;
      this.toggleAttribute('item-added', false);
      this.#syncAddedMessageAfterRender();
    }

    if (isProductAdd && this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #selectReferenceLineItem() {
    if (!this.classList.contains('cart-drawer--reference')) return;

    const lineItems = Array.from(this.querySelectorAll('[data-cart-drawer-reference-line]'));

    for (const lineItem of lineItems) {
      if (lineItem instanceof HTMLElement) {
        lineItem.hidden = false;
      }
    }

    this.querySelectorAll('[data-cart-drawer-reference-related-add]').forEach((button) => {
      if (button instanceof HTMLButtonElement && !button.closest('form')?.querySelector('[name="selling_plan"]')) {
        button.disabled = false;
      }
    });
  }

  #syncAddedMessage() {
    if (!this.classList.contains('cart-drawer--reference')) return;

    const cartItemCount = Number.parseInt(this.querySelector('[ref="cartItemCount"]')?.textContent || '0', 10);
    const hasReferenceItems = cartItemCount > 0 || this.querySelector('[data-cart-drawer-reference-line]');
    const shouldShowAddedMessage = Boolean(window[CART_DRAWER_REFERENCE_ADDED_FLAG] || hasReferenceItems);

    this.toggleAttribute('item-added', shouldShowAddedMessage);
  }

  #syncAddedMessageAfterRender() {
    this.#syncAddedMessage();

    [0, 50, 150, 350].forEach((delay) => {
      setTimeout(() => {
        this.#syncAddedMessage();
      }, delay);
    });
  }

  #stopReferenceControlClick = (event) => {
    if (!this.classList.contains('cart-drawer--reference')) return;
    if (!(event.target instanceof Element)) return;

    if (event.target.closest('[data-cart-drawer-reference-control]')) {
      event.stopImmediatePropagation();
    }
  };

  #handleReferenceRelatedClick = (event) => {
    if (!this.classList.contains('cart-drawer--reference')) return;
    if (!(event.target instanceof Element)) return;

    const submitter = event.target.closest('[data-cart-drawer-reference-related-add]');
    if (!(submitter instanceof HTMLButtonElement)) return;

    const form = submitter.closest('.cart-drawer-reference__related-form');
    if (!(form instanceof HTMLFormElement)) return;

    event.preventDefault();
    event.stopPropagation();
    this.#addRelatedProductForm(form, submitter);
  };

  #handleReferenceRelatedSubmit = (event) => {
    if (!(event.target instanceof HTMLFormElement)) return;
    if (!event.target.matches('.cart-drawer-reference__related-form')) return;

    event.preventDefault();
    const submitter = event.submitter instanceof HTMLButtonElement ? event.submitter : null;
    this.#addRelatedProductForm(event.target, submitter);
  };

  open() {
    this.#syncAddedMessage();
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.#syncAddedMessage();
    this.closeDialog();
  }

  /**
   * Adds a related product from the reference drawer rail.
   * @param {SubmitEvent} event
   */
  async addRelatedProduct(event) {
    event.preventDefault();

    if (!(event.target instanceof HTMLFormElement)) return;

    const submitter = event.submitter instanceof HTMLButtonElement ? event.submitter : null;
    this.#addRelatedProductForm(event.target, submitter);
  }

  /**
   * Adds a related product form and refreshes all cart item sections.
   * @param {HTMLFormElement} form
   * @param {HTMLButtonElement | null} submitter
   */
  async #addRelatedProductForm(form, submitter = null) {
    if (form.getAttribute('aria-busy') === 'true') return;

    const formData = new FormData(form);
    const variantId = formData.get('id')?.toString();

    if (!variantId) return;

    const sectionsToUpdate = this.#getCartSectionsToUpdate();
    const existingLineItem = this.#getReferenceLineItemByVariant(variantId);
    const quantity = Number(formData.get('quantity')) || 1;

    formData.set('sections', Array.from(sectionsToUpdate).join(','));
    formData.set('sections_url', window.location.pathname);

    submitter?.setAttribute('disabled', '');
    form.setAttribute('aria-busy', 'true');

    try {
      const parsedResponse = existingLineItem
        ? await this.#changeRelatedProductQuantity(existingLineItem, quantity, sectionsToUpdate)
        : await this.#addRelatedProductToCart(formData);

      this.#lastAddedVariantId = variantId;
      window[CART_DRAWER_REFERENCE_ADDED_FLAG] = true;
      this.toggleAttribute('item-added', true);
      this.#syncAddedMessageAfterRender();
      this.dispatchEvent(
        new CartAddEvent(parsedResponse, variantId, {
          source: 'product-form-component',
          itemCount: quantity,
          variantId,
          sections: parsedResponse.sections,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      submitter?.removeAttribute('disabled');
      form.removeAttribute('aria-busy');
    }
  }

  #getCartSectionsToUpdate() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set();

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    return sectionsToUpdate;
  }

  /**
   * @param {string} variantId
   * @returns {HTMLElement | null}
   */
  #getReferenceLineItemByVariant(variantId) {
    const lineItems = Array.from(this.querySelectorAll('[data-cart-drawer-reference-line]'));

    return lineItems.find((lineItem) => lineItem instanceof HTMLElement && lineItem.dataset.variantId === variantId) || null;
  }

  /**
   * @param {FormData} formData
   */
  async #addRelatedProductToCart(formData) {
    const fetchCfg = fetchConfig('javascript', { body: formData });
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'application/json',
      },
    });
    const parsedResponse = await response.json();

    if (!response.ok || parsedResponse.status) {
      throw new Error(parsedResponse.description || parsedResponse.message || 'Unable to add related product');
    }

    return parsedResponse;
  }

  /**
   * @param {HTMLElement} lineItem
   * @param {number} quantityToAdd
   * @param {Set<string>} sectionsToUpdate
   */
  async #changeRelatedProductQuantity(lineItem, quantityToAdd, sectionsToUpdate) {
    const line = Number(lineItem.dataset.lineNumber);
    const currentQuantity = Number(lineItem.dataset.quantity) || 0;

    if (!line) throw new Error('Unable to find related product cart line');

    const body = JSON.stringify({
      line,
      quantity: currentQuantity + quantityToAdd,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
    const parsedResponse = await response.json();

    if (!response.ok || parsedResponse.errors) {
      throw new Error(parsedResponse.errors || parsedResponse.description || 'Unable to update related product quantity');
    }

    return parsedResponse;
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
